import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TAGS = ["Release", "Update", "Announcement"];
const empty = { program_id: "", tag: "Update", title: "", description: "", date: "" };

export default function AdminWhatsNew({ refs }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [progFilter, setProgFilter] = useState("all");

  const load = () => {
    const reqs = refs.programs.map((p) => api.get("/announcements", { params: { program: p.id } }));
    Promise.all(reqs).then((rs) => {
      const all = rs.flatMap((r) => r.data);
      const seen = {}; all.forEach((a) => { seen[a.id] = a; });
      setItems(Object.values(seen).sort((a, b) => (b.date || "").localeCompare(a.date || "")));
    }).catch(() => {});
  };
  useEffect(() => { if (refs.programs.length) load(); }, [refs.programs.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const targets = refs.managedPrograms;

  const openCreate = () => { setEditing(null); setForm({ ...empty, program_id: targets[0]?.id || "" }); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...empty, ...a }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/announcements/${editing.id}`, form);
      else await api.post("/announcements", form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (a) => { await api.delete(`/announcements/${a.id}`); toast.success("Deleted"); load(); };

  const filtered = progFilter === "all" ? items : items.filter((i) => i.program_id === progFilter);

  return (
    <div>
      <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
        <Select value={progFilter} onValueChange={setProgFilter}>
          <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All programs</SelectItem>
            {refs.programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={openCreate} data-testid="admin-create-announcement"><Plus className="w-4 h-4 mr-1.5" /> New Update</Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="admin-announcements-list">
        {filtered.map((a) => (
          <div key={a.id} className="flex items-center gap-3 p-4">
            <Sparkles className="w-4 h-4 text-primary shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-800">{a.title}</p>
              <p className="text-xs text-slate-400">{refs.programName(a.program_id)} · {a.date}</p>
            </div>
            <Badge variant="outline">{a.tag}</Badge>
            <Button size="icon" variant="ghost" onClick={() => openEdit(a)} data-testid={`edit-announcement-${a.id}`}><Pencil className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" className="text-rose-500" onClick={() => remove(a)} data-testid={`delete-announcement-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        {filtered.length === 0 && <p className="p-8 text-center text-sm text-slate-400">No updates posted.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit Update" : "New What's New Entry"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Program</Label>
                <Select value={form.program_id} onValueChange={(v) => setForm({ ...form, program_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="announcement-program"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{targets.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.tag} onValueChange={(v) => setForm({ ...form, tag: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{TAGS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="announcement-title" /></div>
            <div><Label>Body</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1" /></div>
            <div><Label>Date (optional)</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="announcement-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
